import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { pickAirportBg, CITY_SEED_PHOTOS } from "../../lib/assets";

/* CuratedRail — horizontal "Curated Journeys" scroller at the top of Destinations.
   Pulls only is_featured items from the passed `items` array (no extra fetch —
   the parent already loaded them). Cream+gold editorial styling matches the
   destination card redesign. Auto-hides when no featured items are available. */
function destinationImage(d) {
  return (CITY_SEED_PHOTOS && CITY_SEED_PHOTOS[d.name]) || pickAirportBg(d.name);
}

export default function CuratedRail({ items = [] }) {
  const featured = (items || []).filter((d) => d.is_featured);
  const scrollerRef = useRef(null);
  const [pos, setPos] = useState({ atStart: true, atEnd: false });

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const check = () => {
      setPos({
        atStart: el.scrollLeft <= 4,
        atEnd: el.scrollLeft + el.clientWidth >= el.scrollWidth - 4,
      });
    };
    check();
    el.addEventListener("scroll", check, { passive: true });
    return () => el.removeEventListener("scroll", check);
  }, [featured.length]);

  if (featured.length === 0) return null;

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(320, el.clientWidth * 0.85), behavior: "smooth" });
  };

  return (
    <section className="mb-10" data-testid="curated-rail">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-amber-700 inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Editor's Selection
          </div>
          <h2 className="mt-1 font-serif font-800 text-3xl sm:text-4xl text-slate-900 leading-tight tracking-tight">
            Curated <span className="italic text-amber-700">Journeys</span>
          </h2>
          <p className="text-sm text-slate-600 mt-1 max-w-lg">
            Our hand-picked destinations — the ones we'd book ourselves this season.
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-1.5">
          <button onClick={() => scrollBy(-1)} disabled={pos.atStart} data-testid="curated-prev"
            className="w-9 h-9 grid place-items-center rounded-full border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-30 disabled:cursor-not-allowed transition">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={() => scrollBy(1)} disabled={pos.atEnd} data-testid="curated-next"
            className="w-9 h-9 grid place-items-center rounded-full border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-30 disabled:cursor-not-allowed transition">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div ref={scrollerRef}
        className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-3 -mx-5 px-5 lg:mx-0 lg:px-0 scrollbar-none"
        style={{ scrollbarWidth: "none" }}>
        {featured.map((d) => (
          <Link key={d.id} to={`/destinations/${d.id}`} data-testid={`curated-item-${d.id}`}
            className="group snap-start shrink-0 w-[280px] sm:w-[320px] rounded-2xl overflow-hidden border-2 border-amber-200 hover:border-amber-500 hover:shadow-xl hover:-translate-y-1 transition-all bg-white">
            <div className="relative aspect-[16/10] overflow-hidden">
              <img src={destinationImage(d)} alt={d.name} loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
              <div className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[9px] font-mono uppercase tracking-[0.22em] shadow-sm">
                Featured
              </div>
              <div className="absolute inset-x-0 bottom-0 p-4">
                <div className="text-[9px] font-mono uppercase tracking-[0.22em] text-amber-200">{d.country}</div>
                <div className="font-serif font-800 text-2xl text-white leading-[1.05] drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">{d.name}</div>
              </div>
            </div>
            <div className="p-3.5 flex items-center justify-between" style={{ background: "linear-gradient(180deg, #faf7f0 0%, #ffffff 100%)" }}>
              <div className="text-xs italic text-slate-700 line-clamp-1 pr-2">{d.tagline}</div>
              <span className="inline-flex items-center gap-0.5 text-amber-700 text-xs font-800 group-hover:gap-1.5 transition-all shrink-0">
                Open <ArrowUpRight className="w-3 h-3" />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
